import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCRM } from '../../context/CRMContext';
import { apiFetch, getFileUrl } from '../../utils/api';
import Header from '../../components/layout/Header';
import { Button, Modal } from '../../components/common/Common';
import {
  Save, ArrowLeft, User, Phone, Mail, IndianRupee, Percent,
  Calendar, Building2, Hash, FileText, CheckCircle2, AlertCircle, Calculator,
  Landmark, UserCheck, Home, Search, X, Loader2,
  Paperclip, Upload, ExternalLink, Download, Trash2,
} from 'lucide-react';
import { isValidPhoneString, phoneStringHint } from '../../data/countryCodes';
import './AddLoan.css';

const LOAN_TYPES = ['Home Loan', 'Personal Loan', 'Business Loan', 'Auto Loan', 'Education Loan'];
const STATUSES = ['Pending', 'Approved', 'Rejected', 'Active', 'Closed'];
const DOCUMENT_TYPES = ['KYC', 'Income Proof', 'Property Documents', 'Sanction Letter', 'Agreement', 'Disbursement Letter', 'Other'];
const DOC_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const MAX_DOC_SIZE = 5 * 1024 * 1024; // must match backend upload limit

const formatFileSize = (bytes) => {
  const n = Number(bytes || 0);
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

const emptyForm = {
  applicant_name: '',
  applicant_phone: '',
  applicant_email: '',
  loan_amount: '',
  loan_type: 'Home Loan',
  tenure_months: '240',
  interest_rate: '',
  status: 'Pending',
  lender_name: '',
  loan_reference: '',
  approval_date: '',
  start_date: '',
  emi_amount: '',
  notes: '',
  project_id: '',
  date_of_booking: '',
  unit_details: '',
  bank_id: '',
  sales_manager_id: '',
};

// EMI formula: P * r * (1+r)^n / ((1+r)^n - 1), r = monthly rate
const calculateEmi = (principal, ratePct, months) => {
  const p = Number(principal || 0);
  const r = Number(ratePct || 0) / 12 / 100;
  const n = Number(months || 0);
  if (p <= 0 || n <= 0) return 0;
  if (r === 0) return p / n;
  const factor = Math.pow(1 + r, n);
  return (p * r * factor) / (factor - 1);
};

const AddLoan = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const { createLoanRecord, updateLoanRecord, uploadLoanDocument, deleteLoanDocument, fetchUsers, fetchLeads, fetchLead } = useCRM();

  const [form, setForm] = useState({ ...emptyForm });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const [projects, setProjects] = useState([]);
  const [banks, setBanks] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);

  // ── Documents ──
  // Edit: uploaded straight away. Add: queued and uploaded after the loan is created.
  const [documents, setDocuments] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [docType, setDocType] = useState('KYC');
  const [docUploading, setDocUploading] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [docDeleting, setDocDeleting] = useState(false);

  // ── Lead search (autofill) ──
  const [leadQuery, setLeadQuery] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [leadSearching, setLeadSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [autofilling, setAutofilling] = useState(false);
  const [recentLeads, setRecentLeads] = useState([]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Load dropdown options
  const loadOptions = useCallback(async () => {
    try {
      const [projRes, bankRes, userRes] = await Promise.all([
        apiFetch('dynamic-fields/projects'),
        apiFetch('dynamic-fields/bank-names'),
        fetchUsers(),
      ]);
      // dynamic-fields/projects reads lead_projects, whose id column is lp_id
      if (projRes?.success) {
        setProjects((projRes.data || [])
          .filter(p => p.is_active !== 0)
          .map(p => ({ ...p, project_id: p.project_id ?? p.lp_id })));
      }
      if (bankRes?.success) setBanks((bankRes.data || []).filter(b => b.is_active !== 0));
      if (userRes?.success) {
        const u = userRes.data;
        setSalesUsers(Array.isArray(u) ? u : (u?.users || []));
      }
    } catch (e) { console.error(e); }
  }, [fetchUsers]);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  // Re-pull dropdowns when the user returns to this tab (e.g. after adding a
  // project/bank in Dynamic Fields) so options are never stale without a refresh.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadOptions(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadOptions]);

  // Load loan for editing
  useEffect(() => {
    if (!isEditing) return;
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`loans/${id}`);
        const loan = res?.success ? res.data : null;
        if (loan) {
          setForm({
            applicant_name: loan.applicant_name || '',
            applicant_phone: loan.applicant_phone || '',
            applicant_email: loan.applicant_email || '',
            loan_amount: loan.loan_amount ?? '',
            loan_type: loan.loan_type || 'Home Loan',
            tenure_months: loan.tenure_months ?? '',
            interest_rate: loan.interest_rate ?? '',
            status: loan.status || 'Pending',
            lender_name: loan.lender_name || '',
            loan_reference: loan.loan_reference || '',
            approval_date: loan.approval_date ? String(loan.approval_date).slice(0, 10) : '',
            start_date: loan.start_date ? String(loan.start_date).slice(0, 10) : '',
            emi_amount: loan.emi_amount ?? '',
            notes: loan.notes || '',
            project_id: loan.project_id ?? '',
            date_of_booking: loan.date_of_booking ? String(loan.date_of_booking).slice(0, 10) : '',
            unit_details: loan.unit_details || '',
            bank_id: loan.bank_id ?? '',
            sales_manager_id: loan.sales_manager_id ?? '',
          });
          setDocuments(Array.isArray(loan.documents) ? loan.documents : []);
        } else {
          showToast('Loan not found', 'error');
          setTimeout(() => navigate('/loans'), 1000);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [id, isEditing, navigate]);

  // Debounced lead search
  useEffect(() => {
    if (isEditing) return;
    const q = leadQuery.trim();
    if (q.length < 2) {
      setLeadResults([]);
      setLeadSearching(false);
      return;
    }
    setLeadSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetchLeads({ search: q, limit: 20, exclude_imported: false });
        const list = res?.data?.leads || res?.data || [];
        setLeadResults(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        setLeadResults([]);
      }
      setLeadSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [leadQuery, isEditing, fetchLeads]);

  // Load recent leads on mount (always visible under the search box)
  useEffect(() => {
    if (isEditing) return;
    (async () => {
      try {
        const res = await fetchLeads({ limit: 8, exclude_imported: false });
        const list = res?.data?.leads || res?.data || [];
        setRecentLeads(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [isEditing, fetchLeads]);

  const handleLeadSelect = async (lead) => {
    setAutofilling(true);
    try {
      const res = await fetchLead(lead.l_id);
      const full = res?.success ? (res.data?.lead || res.data || lead) : lead;

      // Phone — prefer mobile, fallback to alt_mobile
      const cc = full.country_code || full.alt_country_code || '';
      const mob = full.mobile || full.alt_mobile || '';
      const phone = [cc, mob].filter(Boolean).join(' ').trim();

      // Pick first project_id from any requirement (unconditionally)
      let projectId = '';
      const reqs = Array.isArray(full.requirements) ? full.requirements : [];
      for (const r of reqs) {
        let pids = r.project_ids;
        if (typeof pids === 'string') {
          try { pids = JSON.parse(pids); } catch { pids = []; }
        }
        if (Array.isArray(pids) && pids.length > 0) {
          projectId = String(pids[0]);
          break;
        }
        if (r.project_id) { projectId = String(r.project_id); break; }
      }

      // Pick first assignment as sales manager (findById already filters is_active=1)
      let smId = '';
      const assignments = Array.isArray(full.assignments) ? full.assignments : [];
      if (assignments.length > 0 && assignments[0].u_id) {
        smId = String(assignments[0].u_id);
      }

      // If selected project isn't in dropdown, inject it so it shows
      if (projectId && !projects.some(p => String(p.project_id) === projectId)) {
        const reqWithName = reqs.find(r => r.project_names);
        const projName = reqWithName?.project_names?.split(',')[0]?.trim() || full.project_name || `Project #${projectId}`;
        setProjects(prev => [...prev, { project_id: Number(projectId) || projectId, name: projName, is_active: 1 }]);
      }

      // If assigned user isn't in salesUsers, inject so dropdown shows it
      if (smId && !salesUsers.some(u => String(u.u_id) === smId)) {
        const a = assignments[0];
        setSalesUsers(prev => [...prev, { u_id: a.u_id, username: a.assigned_to_name || `User #${a.u_id}`, name: a.assigned_to_name || '' }]);
      }

      setForm(p => ({
        ...p,
        applicant_name: full.name ?? p.applicant_name,
        applicant_phone: phone || p.applicant_phone,
        applicant_email: full.email ?? p.applicant_email,
        project_id: projectId || p.project_id,
        sales_manager_id: smId || p.sales_manager_id,
      }));
      setErrors(p => ({ ...p, applicant_name: '', applicant_phone: '', applicant_email: '' }));
      setSelectedLead({
        l_id: full.l_id,
        name: full.name,
        mobile: full.mobile,
        country_code: full.country_code,
      });
      setLeadQuery('');
      setLeadResults([]);
      showToast('Lead details filled');
    } catch (err) {
      console.error(err);
      showToast('Failed to load lead', 'error');
    }
    setAutofilling(false);
  };

  const clearSelectedLead = () => {
    setSelectedLead(null);
    setLeadQuery('');
    setLeadResults([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const suggestedEmi = useMemo(
    () => calculateEmi(form.loan_amount, form.interest_rate, form.tenure_months),
    [form.loan_amount, form.interest_rate, form.tenure_months]
  );

  const applySuggestedEmi = () => {
    if (suggestedEmi > 0) {
      setForm(p => ({ ...p, emi_amount: suggestedEmi.toFixed(2) }));
    }
  };

  const handleDocumentPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again
    if (!file) return;

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!DOC_EXTENSIONS.includes(ext)) {
      showToast('Only PDF, JPG, PNG, DOC and DOCX files are allowed', 'error');
      return;
    }
    if (file.size > MAX_DOC_SIZE) {
      showToast('File is larger than 5 MB', 'error');
      return;
    }

    if (!isEditing) {
      setPendingDocs(p => [...p, { key: `${Date.now()}-${file.name}`, file, doc_type: docType }]);
      return;
    }

    setDocUploading(true);
    const res = await uploadLoanDocument(id, file, docType);
    if (res.success) {
      setDocuments(res.data?.documents || []);
      showToast('Document uploaded');
    } else {
      showToast(res.message || 'Failed to upload document', 'error');
    }
    setDocUploading(false);
  };

  const confirmDeleteDocument = async () => {
    if (!docToDelete) return;
    setDocDeleting(true);
    const res = await deleteLoanDocument(id, docToDelete.id);
    if (res.success) {
      setDocuments(res.data?.documents || []);
      showToast('Document deleted');
      setDocToDelete(null);
    } else {
      showToast(res.message || 'Failed to delete document', 'error');
    }
    setDocDeleting(false);
  };

  const validate = () => {
    const e = {};
    if (!form.applicant_name.trim()) e.applicant_name = 'Applicant name is required';
    if (!form.applicant_phone.trim()) e.applicant_phone = 'Phone is required';
    else if (!isValidPhoneString(form.applicant_phone)) {
      // The country code lives inside this field, so the rule comes from
      // whatever dial code was typed (falls back to 7–15 when none).
      e.applicant_phone = `Enter a valid phone number (${phoneStringHint(form.applicant_phone)} digits after the country code)`;
    }
    if (form.applicant_email && !/^\S+@\S+\.\S+$/.test(form.applicant_email)) {
      e.applicant_email = 'Enter a valid email';
    }
    if (form.loan_amount === '' || Number(form.loan_amount) <= 0) {
      e.loan_amount = 'Loan amount is required and must be > 0';
    }
    if (!form.loan_type) e.loan_type = 'Loan type is required';
    if (form.interest_rate && Number(form.interest_rate) < 0) e.interest_rate = 'Cannot be negative';
    if (form.tenure_months && Number(form.tenure_months) <= 0) e.tenure_months = 'Must be > 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      showToast('Please fix the highlighted fields', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        loan_amount: Number(form.loan_amount) || 0,
        tenure_months: Number(form.tenure_months) || 0,
        interest_rate: form.interest_rate === '' ? null : Number(form.interest_rate),
        emi_amount: form.emi_amount === '' ? null : Number(form.emi_amount),
        approval_date: form.approval_date || null,
        start_date: form.start_date || null,
        project_id: form.project_id ? Number(form.project_id) : null,
        date_of_booking: form.date_of_booking || null,
        unit_details: form.unit_details || null,
        bank_id: form.bank_id ? Number(form.bank_id) : null,
        sales_manager_id: form.sales_manager_id ? Number(form.sales_manager_id) : null,
      };
      if (!isEditing && selectedLead?.l_id) payload.lead_id = selectedLead.l_id;
      const res = isEditing
        ? await updateLoanRecord(id, payload)
        : await createLoanRecord(payload);

      if (res.success) {
        let failed = 0;
        const newLoanId = res.data?.loan_id;
        if (!isEditing && newLoanId && pendingDocs.length > 0) {
          for (const d of pendingDocs) {
            const up = await uploadLoanDocument(newLoanId, d.file, d.doc_type);
            if (!up.success) failed += 1;
          }
        }
        if (failed > 0) {
          showToast(`Loan created, but ${failed} document(s) failed to upload`, 'error');
          setTimeout(() => navigate('/loans'), 2500);
        } else {
          showToast(isEditing ? 'Loan updated' : 'Loan created');
          setTimeout(() => navigate('/loans'), 800);
        }
      } else {
        showToast(res.message || 'Failed to save loan', 'error');
      }
    } catch {
      showToast('Failed to save loan', 'error');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div>
        <Header title="Loan" subtitle="Loading..." />
        <div className="page"><div className="al-loading">Loading loan...</div></div>
      </div>
    );
  }

  return (
    <div>
      <Header
        title={isEditing ? 'Edit Loan' : 'Add Loan'}
        subtitle={isEditing ? 'Update loan details' : 'Create a new loan application'}
        actions={<Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/loans')}>Back</Button>}
      />
      <div className="page">
        <div className={`al-layout ${isEditing ? 'al-layout--single' : ''}`}>
        <form className="al-form al-layout__main" onSubmit={handleSubmit}>
          {/* ── Applicant ── */}
          <section className="al-card">
            <div className="al-card__head">
              <div className="al-card__icon"><User size={18} /></div>
              <div>
                <h3>Applicant</h3>
                <p>Who is applying for the loan</p>
              </div>
            </div>
            <div className="al-grid">
              <div className={`al-field ${errors.applicant_name ? 'al-field--error' : ''}`}>
                <label>Client Name *</label>
                <div className="al-input"><User size={14} />
                  <input
                    name="applicant_name"
                    value={form.applicant_name}
                    onChange={handleChange}
                    placeholder="Full name"
                    required
                  />
                </div>
                {errors.applicant_name && <span className="al-err"><AlertCircle size={11} /> {errors.applicant_name}</span>}
              </div>

              <div className={`al-field ${errors.applicant_phone ? 'al-field--error' : ''}`}>
                <label>Mobile No. *</label>
                <div className="al-input"><Phone size={14} />
                  <input
                    name="applicant_phone"
                    value={form.applicant_phone}
                    onChange={handleChange}
                    placeholder="e.g. +91 98765 43210"
                    required
                  />
                </div>
                {errors.applicant_phone && <span className="al-err"><AlertCircle size={11} /> {errors.applicant_phone}</span>}
              </div>

              <div className={`al-field ${errors.applicant_email ? 'al-field--error' : ''}`}>
                <label>Email</label>
                <div className="al-input"><Mail size={14} />
                  <input
                    type="email"
                    name="applicant_email"
                    value={form.applicant_email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                  />
                </div>
                {errors.applicant_email && <span className="al-err"><AlertCircle size={11} /> {errors.applicant_email}</span>}
              </div>
            </div>
          </section>

          {/* ── Booking & Sales ── */}
          <section className="al-card">
            <div className="al-card__head">
              <div className="al-card__icon al-card__icon--blue"><Home size={18} /></div>
              <div>
                <h3>Booking & Sales</h3>
                <p>Project, unit and assigned sales manager</p>
              </div>
            </div>
            <div className="al-grid">
              <div className="al-field">
                <label>Project Name</label>
                <div className="al-input"><Building2 size={14} />
                  <select name="project_id" value={form.project_id} onChange={handleChange}>
                    <option value="">Select Project Name</option>
                    {projects.map(p => (
                      <option key={p.project_id} value={p.project_id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="al-field">
                <label>Date of Booking</label>
                <div className="al-input"><Calendar size={14} />
                  <input
                    type="date"
                    name="date_of_booking"
                    value={form.date_of_booking}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="al-field">
                <label>Unit Details</label>
                <div className="al-input"><Hash size={14} />
                  <input
                    name="unit_details"
                    value={form.unit_details}
                    onChange={handleChange}
                    placeholder="e.g. A-1204, Tower-3"
                  />
                </div>
              </div>

              <div className="al-field">
                <label>Sales Manager</label>
                <div className="al-input"><UserCheck size={14} />
                  <select name="sales_manager_id" value={form.sales_manager_id} onChange={handleChange}>
                    <option value="">Select Sales Manager</option>
                    {salesUsers.map(u => (
                      <option key={u.u_id} value={u.u_id}>
                        {(u.name && u.name.trim()) || u.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* ── Loan Details ── */}
          <section className="al-card">
            <div className="al-card__head">
              <div className="al-card__icon al-card__icon--gold"><IndianRupee size={18} /></div>
              <div>
                <h3>Loan Details</h3>
                <p>Amount, type & tenure</p>
              </div>
            </div>
            <div className="al-grid">
              <div className={`al-field ${errors.loan_amount ? 'al-field--error' : ''}`}>
                <label>Sanction Amount *</label>
                <div className="al-input"><IndianRupee size={14} />
                  <input
                    type="number"
                    name="loan_amount"
                    value={form.loan_amount}
                    onChange={handleChange}
                    placeholder="e.g. 5000000"
                    min="0"
                    required
                  />
                </div>
                {errors.loan_amount && <span className="al-err"><AlertCircle size={11} /> {errors.loan_amount}</span>}
              </div>

              <div className="al-field">
                <label>Loan Type *</label>
                <div className="al-input"><Building2 size={14} />
                  <select name="loan_type" value={form.loan_type} onChange={handleChange} required>
                    {LOAN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className={`al-field ${errors.tenure_months ? 'al-field--error' : ''}`}>
                <label>Tenure (Months)</label>
                <div className="al-input"><Calendar size={14} />
                  <input
                    type="number"
                    name="tenure_months"
                    value={form.tenure_months}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                {errors.tenure_months && <span className="al-err"><AlertCircle size={11} /> {errors.tenure_months}</span>}
              </div>

              <div className={`al-field ${errors.interest_rate ? 'al-field--error' : ''}`}>
                <label>Interest Rate (% per year)</label>
                <div className="al-input"><Percent size={14} />
                  <input
                    type="number"
                    step="0.01"
                    name="interest_rate"
                    value={form.interest_rate}
                    onChange={handleChange}
                    placeholder="e.g. 8.5"
                    min="0"
                  />
                </div>
                {errors.interest_rate && <span className="al-err"><AlertCircle size={11} /> {errors.interest_rate}</span>}
              </div>

              <div className="al-field">
                <label>EMI Amount (Monthly)</label>
                <div className="al-input"><IndianRupee size={14} />
                  <input
                    type="number"
                    step="0.01"
                    name="emi_amount"
                    value={form.emi_amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                  />
                </div>
                {suggestedEmi > 0 && (
                  <button type="button" className="al-suggest" onClick={applySuggestedEmi}>
                    <Calculator size={12} /> Use suggested: ₹{suggestedEmi.toFixed(2)}
                  </button>
                )}
              </div>

              <div className="al-field">
                <label>Loan Status</label>
                <div className="al-input"><CheckCircle2 size={14} />
                  <select name="status" value={form.status} onChange={handleChange}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* ── Lender ── */}
          <section className="al-card">
            <div className="al-card__head">
              <div className="al-card__icon al-card__icon--blue"><Building2 size={18} /></div>
              <div>
                <h3>Lender & Schedule</h3>
                <p>Bank / NBFC and key dates</p>
              </div>
            </div>
            <div className="al-grid">
              <div className="al-field">
                <label>Bank Name</label>
                <div className="al-input"><Landmark size={14} />
                  <select name="bank_id" value={form.bank_id} onChange={handleChange}>
                    <option value="">Select Bank Name</option>
                    {banks.map(b => (
                      <option key={b.bank_id} value={b.bank_id}>{b.bank_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="al-field">
                <label>Lender Name</label>
                <div className="al-input"><Building2 size={14} />
                  <input
                    name="lender_name"
                    value={form.lender_name}
                    onChange={handleChange}
                    placeholder="Bank or NBFC (free text)"
                  />
                </div>
              </div>

              <div className="al-field">
                <label>Loan Reference</label>
                <div className="al-input"><Hash size={14} />
                  <input
                    name="loan_reference"
                    value={form.loan_reference}
                    onChange={handleChange}
                    placeholder="Reference number"
                  />
                </div>
              </div>

              <div className="al-field">
                <label>Approval Date</label>
                <div className="al-input"><Calendar size={14} />
                  <input
                    type="date"
                    name="approval_date"
                    value={form.approval_date}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="al-field">
                <label>Start Date</label>
                <div className="al-input"><Calendar size={14} />
                  <input
                    type="date"
                    name="start_date"
                    value={form.start_date}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* ── Documents ── */}
          <section className="al-card">
            <div className="al-card__head">
              <div className="al-card__icon al-card__icon--gold"><Paperclip size={18} /></div>
              <div>
                <h3>Documents</h3>
                <p>KYC, income proof, sanction letter and other loan papers</p>
              </div>
            </div>

            <div className="al-doc-upload">
              <div className="al-field">
                <label>Document Type</label>
                <div className="al-input"><FileText size={14} />
                  <select value={docType} onChange={(e) => setDocType(e.target.value)}>
                    {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <label className={`al-doc-pick ${docUploading ? 'al-doc-pick--busy' : ''}`}>
                {docUploading ? <Loader2 size={14} className="al-spin" /> : <Upload size={14} />}
                {docUploading ? 'Uploading...' : 'Choose File'}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleDocumentPick}
                  disabled={docUploading}
                  hidden
                />
              </label>
              <span className="al-doc-hint">PDF, JPG, PNG, DOC or DOCX, up to 5 MB</span>
            </div>

            {documents.length === 0 && pendingDocs.length === 0 ? (
              <div className="al-doc-empty">No documents added yet</div>
            ) : (
              <ul className="al-doc-list">
                {documents.map(d => (
                  <li key={d.id || d.file_path} className="al-doc">
                    <div className="al-doc__icon"><FileText size={16} /></div>
                    <div className="al-doc__info">
                      <span className="al-doc__name" title={d.name}>{d.name}</span>
                      <span className="al-doc__meta">
                        <span className="al-doc__type">{d.doc_type || 'Other'}</span>
                        {formatFileSize(d.size)}
                        {d.uploaded_at && ` · ${new Date(d.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                        {d.uploaded_by_name && ` · ${d.uploaded_by_name}`}
                      </span>
                    </div>
                    <div className="al-doc__actions">
                      <a className="al-doc__btn" href={getFileUrl(d.file_path)} target="_blank" rel="noopener noreferrer" title="View">
                        <ExternalLink size={14} />
                      </a>
                      <a
                        className="al-doc__btn"
                        href={getFileUrl(`/download?path=${encodeURIComponent(d.file_path)}&name=${encodeURIComponent(d.name || 'document')}`)}
                        title="Download"
                      >
                        <Download size={14} />
                      </a>
                      {d.id && (
                        <button type="button" className="al-doc__btn al-doc__btn--danger" title="Delete" onClick={() => setDocToDelete(d)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
                {pendingDocs.map(d => (
                  <li key={d.key} className="al-doc al-doc--pending">
                    <div className="al-doc__icon"><FileText size={16} /></div>
                    <div className="al-doc__info">
                      <span className="al-doc__name" title={d.file.name}>{d.file.name}</span>
                      <span className="al-doc__meta">
                        <span className="al-doc__type">{d.doc_type}</span>
                        {formatFileSize(d.file.size)} · Uploads when the loan is created
                      </span>
                    </div>
                    <div className="al-doc__actions">
                      <button
                        type="button"
                        className="al-doc__btn al-doc__btn--danger"
                        title="Remove"
                        onClick={() => setPendingDocs(p => p.filter(x => x.key !== d.key))}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ── Notes ── */}
          <section className="al-card">
            <div className="al-card__head">
              <div className="al-card__icon al-card__icon--gray"><FileText size={18} /></div>
              <div>
                <h3>Notes</h3>
                <p>Anything else worth remembering</p>
              </div>
            </div>
            <textarea
              className="al-notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Add notes about this loan..."
            />
          </section>

          <div className="al-actions">
            <Button variant="outline" onClick={() => navigate('/loans')}>Cancel</Button>
            <Button variant="gold" icon={Save} type="submit" disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Loan'}
            </Button>
          </div>
        </form>

        {!isEditing && (
          <aside className="al-layout__side">
            <section className="al-card al-lead-search-card">
              <div className="al-card__head">
                <div className="al-card__icon al-card__icon--blue"><Search size={18} /></div>
                <div>
                  <h3>Search Existing Lead</h3>
                  <p>Pick a lead to auto-fill applicant, project & sales manager</p>
                </div>
              </div>

              {selectedLead ? (
                <div className="al-lead-chip">
                  <div className="al-lead-chip__info">
                    <User size={14} />
                    <span className="al-lead-chip__name">{selectedLead.name || 'Lead'}</span>
                    {selectedLead.mobile && (
                      <span className="al-lead-chip__mobile">
                        · {[selectedLead.country_code, selectedLead.mobile].filter(Boolean).join(' ')}
                      </span>
                    )}
                  </div>
                  <button type="button" className="al-lead-chip__clear" onClick={clearSelectedLead}>
                    <X size={14} /> Change
                  </button>
                </div>
              ) : (
                <div className="al-lead-search">
                  <div className="al-input">
                    <Search size={14} />
                    <input
                      type="text"
                      value={leadQuery}
                      onChange={(e) => setLeadQuery(e.target.value)}
                      placeholder="Search by name, mobile or email"
                      autoComplete="off"
                    />
                    {(leadSearching || autofilling) && <Loader2 size={14} className="al-spin" />}
                  </div>
                  {(() => {
                    const isSearch = leadQuery.trim().length >= 2;
                    const list = isSearch ? leadResults : recentLeads;
                    return (
                      <div className="al-lead-panel">
                        <div className="al-lead-dd-head">
                          {isSearch ? 'Search results' : 'Recent leads'}
                        </div>
                        <div className="al-lead-panel__list">
                          {isSearch && leadSearching ? (
                            <div className="al-lead-empty">Searching...</div>
                          ) : list.length === 0 ? (
                            <div className="al-lead-empty">
                              {isSearch ? 'No leads found' : 'No recent leads'}
                            </div>
                          ) : (
                            list.map(l => (
                              <button
                                type="button"
                                key={l.l_id}
                                className="al-lead-item"
                                onClick={() => handleLeadSelect(l)}
                              >
                                <div className="al-lead-item__name">
                                  <User size={12} /> {l.name || 'Unnamed lead'}
                                </div>
                                <div className="al-lead-item__meta">
                                  {l.mobile && (
                                    <span><Phone size={11} /> {[l.country_code, l.mobile].filter(Boolean).join(' ')}</span>
                                  )}
                                  {l.email && (
                                    <span><Mail size={11} /> {l.email}</span>
                                  )}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </section>
          </aside>
        )}
        </div>
      </div>

      <Modal isOpen={!!docToDelete} onClose={() => !docDeleting && setDocToDelete(null)} title="Delete Document" size="sm">
        <p className="al-doc-confirm">
          Delete <strong>{docToDelete?.name}</strong>? This cannot be undone.
        </p>
        <div className="al-actions">
          <Button type="button" variant="outline" onClick={() => setDocToDelete(null)} disabled={docDeleting}>Cancel</Button>
          <Button type="button" variant="danger" icon={Trash2} onClick={confirmDeleteDocument} disabled={docDeleting}>
            {docDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>

      {toast && (
        <div className={`al-toast al-toast--${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
};

export default AddLoan;
